import type { ReactNode } from 'react';
import { Button, Text, Tooltip } from '@coreai/fluentui-react';
import { ArrowClockwiseRegular } from '@fluentui/react-icons';
import { useMonitoringListHeaderStyles } from './MonitoringListHeader.styles';

type MonitoringListHeaderProps = {
  /** Number to display before the unit label. */
  count: number;
  /** Singular unit label — gets an `s` appended automatically when count !== 1. */
  unit: string;
  /** Optional custom actions rendered before the standard refresh button (e.g. "Test draft"). */
  actions?: ReactNode;
  /** Wires up a built-in refresh icon button. */
  onRefresh?: () => void;
  isRefreshing?: boolean;
  refreshAriaLabel?: string;
};

/**
 * Toolbar shown above a monitoring list: `<count> <unit>` on the left,
 * custom + standard actions on the right. Shared by the run history and
 * trigger history surfaces so both look identical.
 */
export const MonitoringListHeader = ({
  count,
  unit,
  actions,
  onRefresh,
  isRefreshing = false,
  refreshAriaLabel,
}: MonitoringListHeaderProps) => {
  const styles = useMonitoringListHeaderStyles();
  const label = `${unit}${count !== 1 ? 's' : ''}`;
  const refreshLabel =
    refreshAriaLabel ?? (isRefreshing ? `Refreshing ${label}` : `Refresh ${label}`);

  return (
    <div className={styles.root}>
      <Text className={styles.count}>
        <span className={styles.countNumber}>{count}</span> {label}
      </Text>
      <div className={styles.actions}>
        {actions}
        {onRefresh && (
          <Tooltip content="Refresh" relationship="label">
            <Button
              appearance="subtle"
              size="small"
              icon={
                isRefreshing ? (
                  <ArrowClockwiseRegular className={styles.refreshIconSpinning} />
                ) : (
                  <ArrowClockwiseRegular />
                )
              }
              onClick={onRefresh}
              disabled={isRefreshing}
              aria-label={refreshLabel}
              aria-busy={isRefreshing}
            />
          </Tooltip>
        )}
      </div>
    </div>
  );
};
